
import React, { useState, useMemo } from 'react';
import { usePromotionProducts } from '../../cart/hooks/usePromotionProducts';
import { useCartContext } from '../../cart';

// ─── Field accessor helpers ────────────────────────────────────────────────────
// The API returns camelCase. These guard against legacy snake_case entries so the
// widget keeps working if the serialisation format ever changes, and avoids
// repeating the `|| fallback` pattern inline throughout the component.
const tierStacking      = (t) => t.stackingType      || t.stacking_type       || 'PARALLEL';
const tierScopeValues   = (t) => t.scopeValues        || t.scope_values        || '';
const tierExclusiveGroup= (t) => t.exclusiveGroup     || t.exclusive_group     || null;
const tierMinQty        = (t) => t.minQuantity        ?? t.min_quantity        ?? 0;
const tierMinAmount     = (t) => t.minAmount          ?? t.min_amount          ?? null;

/**
 * Shows "Ofertas combinables" for PRODUCT-scoped cart promotions.
 * Lets the user pick quantities across multiple products to reach a discount threshold.
 *
 * Stacking types handled:
 *   PARALLEL          – multiple discounts add independently on base price
 *   CASCADE           – each discount applies on the already-reduced price
 *   OVERRIDE          – applies % directly on catalog price (bypasses all ERP discounts)
 *   OVERRIDE_NEGOTIATED – applies % on d1-d4 negotiated price (bypasses d5-d9 BC offers)
 *   OVERRIDE_FULL     – fixed price; excluded from widget UI (handled at cart/checkout level)
 */
export const OfertasCombinables = ({ promotions, catalogProducts = [], sellingPricePerUnit, onActiveItemsChange }) => {

    // ── Price helpers ────────────────────────────────────────────────────────
    const getProductPrice = React.useCallback((groupProduct) => {
        const catalogProduct = catalogProducts.find(cp =>
            String(cp.id) === String(groupProduct.id)
        );
        const isMainProduct = catalogProduct && (
            catalogProduct.code === groupProduct.codigoArticulo ||
            catalogProduct.codigoArticulo === groupProduct.codigoArticulo
        );
        if (isMainProduct && sellingPricePerUnit != null) {
            return sellingPricePerUnit;
        }
        return catalogProduct?.price?.sellingPrice
            ?? catalogProduct?.price?.basePrice
            ?? groupProduct.precioVenta
            ?? groupProduct.precioBase
            ?? 0;
    }, [catalogProducts, sellingPricePerUnit]);

    const getProductOriginalPrice = React.useCallback((groupProduct) => {
        const catalogProduct = catalogProducts.find(cp =>
            String(cp.id) === String(groupProduct.id)
        );
        return catalogProduct?.price?.value
            ?? catalogProduct?.price?.basePrice
            ?? groupProduct.precioBase
            ?? getProductPrice(groupProduct);
    }, [catalogProducts, getProductPrice]);

    // ── Promotion list (PRODUCT-scoped, discount-bearing, exclude OVERRIDE_FULL) ──
    // OVERRIDE_FULL uses a fixed price rather than a percentage; the widget cannot
    // display per-product pricing for it. Those promos still apply at cart/checkout.
    const productPromos = useMemo(() =>
        (promotions ?? [])
            .filter(p => p.scope === 'PRODUCT'
                && p.discountPercentage
                && tierStacking(p) !== 'OVERRIDE_FULL')
            .sort((a, b) => {
                const ama = tierMinAmount(a), bma = tierMinAmount(b);
                if (ama || bma) return (ama ?? 0) - (bma ?? 0);
                return tierMinQty(a) - tierMinQty(b);
            }),
        [promotions]
    );

    const leadPromo = productPromos[0] ?? null;
    const { products: groupProducts } = usePromotionProducts(leadPromo?.id ?? null);

    const [quantities, setQuantities] = useState({});
    const [applying, setApplying]     = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [applied, setApplied]       = useState(false);

    const totalQty = Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0);

    const totalAmount = useMemo(() => {
        let sum = 0;
        Object.entries(quantities).forEach(([codigo, qty]) => {
            if (qty > 0) {
                const gp = groupProducts.find(p => p.codigoArticulo === codigo);
                if (gp) sum += getProductPrice(gp) * qty;
            }
        });
        return sum;
    }, [quantities, groupProducts, getProductPrice]);

    // ── Per-tier threshold helpers ───────────────────────────────────────────
    // Count only units/amount for products in each tier's own scope_values.
    // This prevents products that belong to tier A (but not tier B) from
    // inflating the counter for tier B and triggering the wrong escalated discount.
    const getTierQuantity = React.useCallback((tier) => {
        const sv = tierScopeValues(tier);
        if (!sv) return totalQty;
        const codes = new Set(sv.split(',').map(s => s.trim()).filter(Boolean));
        return Object.entries(quantities).reduce((sum, [codigo, qty]) =>
            codes.has(codigo) ? sum + (qty || 0) : sum, 0);
    }, [quantities, totalQty]);

    const getTierAmount = React.useCallback((tier) => {
        const sv = tierScopeValues(tier);
        if (!sv) return totalAmount;
        const codes = new Set(sv.split(',').map(s => s.trim()).filter(Boolean));
        let sum = 0;
        Object.entries(quantities).forEach(([codigo, qty]) => {
            if (qty > 0 && codes.has(codigo)) {
                const gp = groupProducts.find(p => p.codigoArticulo === codigo);
                if (gp) sum += getProductPrice(gp) * qty;
            }
        });
        return sum;
    }, [quantities, groupProducts, getProductPrice, totalAmount]);

    // ── Active tiers (threshold met) ────────────────────────────────────────
    const activeTiers = useMemo(() =>
        productPromos.filter(p => {
            const ma = tierMinAmount(p);
            if (ma != null && ma > 0) return getTierAmount(p) >= ma;
            return getTierQuantity(p) >= tierMinQty(p);
        }),
        [productPromos, getTierQuantity, getTierAmount]
    );

    // ── Effective tiers (exclusive group filter applied) ─────────────────────
    // Mirrors backend applyExclusiveGroupFilter: among active tiers, only the
    // highest-priority one per exclusive group is kept.
    const effectiveTiers = useMemo(() => {
        const sorted = [...activeTiers].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
        const groupWinners = new Set();
        return sorted.filter(tier => {
            const group = tierExclusiveGroup(tier);
            if (!group) return true;
            if (groupWinners.has(group)) return false;
            groupWinners.add(group);
            return true;
        });
    }, [activeTiers]);

    // d1-d4 negotiated factor for OVERRIDE_NEGOTIATED price calculation.
    // catalog × d1-d4 × (1 - promoDisc%) matches the backend formula exactly.
    const getNegotiatedFactor = React.useCallback((groupProduct) => {
        const catalogProduct = catalogProducts.find(cp => String(cp.id) === String(groupProduct.id));
        const bcd = catalogProduct?.price?.bcDiscounts || groupProduct.bcDiscounts || {};
        return (1 - (bcd.bcDisc1 || 0) / 100)
            * (1 - (bcd.bcDisc2 || 0) / 100)
            * (1 - (bcd.bcDisc3 || 0) / 100)
            * (1 - (bcd.bcDisc4 || 0) / 100);
    }, [catalogProducts]);

    // ── Per-product final price (mirrors backend stacking logic) ────────────
    // Each product gets only the tiers whose scopeValues include that SKU.
    // This prevents a tier that covers only product A from also discounting product B.
    const computeProductFinalPrice = React.useCallback((groupProduct) => {
        const code = groupProduct.codigoArticulo;

        // Filter effectiveTiers to those that actually cover this product
        const applicableTiers = effectiveTiers.filter(tier => {
            const sv = tierScopeValues(tier);
            if (!sv) return true; // no scope restriction → applies to all
            const codes = new Set(sv.split(',').map(s => s.trim()).filter(Boolean));
            return codes.has(code);
        });

        let currentPriceFactor = 1.0;
        let parallelDiscountSum = 0;

        for (const tier of applicableTiers) {
            const stacking = tierStacking(tier);
            const disc = tier.discountPercentage ?? 0;

            if (stacking === 'OVERRIDE_FULL') {
                if (tier.fixedPrice != null) return tier.fixedPrice;
            } else if (stacking === 'CASCADE') {
                currentPriceFactor *= (1 - disc / 100.0);
            } else if (stacking === 'PARALLEL') {
                parallelDiscountSum += disc;
            } else if (stacking === 'OVERRIDE') {
                parallelDiscountSum = disc;
                currentPriceFactor  = 1.0;
            } else if (stacking === 'OVERRIDE_NEGOTIATED') {
                currentPriceFactor *= (1 - disc / 100.0);
            }
        }

        if (parallelDiscountSum > 0) {
            currentPriceFactor *= (1 - parallelDiscountSum / 100.0);
        }

        const basePrice     = getProductPrice(groupProduct);
        const originalPrice = getProductOriginalPrice(groupProduct) || basePrice;

        const hasOverrideNegotiated = applicableTiers.some(t => tierStacking(t) === 'OVERRIDE_NEGOTIATED');
        const hasOverride           = applicableTiers.some(t => tierStacking(t) === 'OVERRIDE');

        if (hasOverrideNegotiated) return originalPrice * getNegotiatedFactor(groupProduct) * currentPriceFactor;
        if (hasOverride)           return originalPrice * currentPriceFactor;
        return basePrice * currentPriceFactor;
    }, [effectiveTiers, getProductPrice, getProductOriginalPrice, getNegotiatedFactor]);

    // ── Next-tier progress ───────────────────────────────────────────────────
    const nextTier = productPromos.find(p => {
        const ma = tierMinAmount(p);
        if (ma != null && ma > 0) return getTierAmount(p) < ma;
        return getTierQuantity(p) < tierMinQty(p);
    }) ?? null;

    const missingForNext = nextTier
        ? (tierMinAmount(nextTier) > 0
            ? `${(tierMinAmount(nextTier) - getTierAmount(nextTier)).toFixed(2).replace('.', ',')} €`
            : `${tierMinQty(nextTier) - getTierQuantity(nextTier)} uds`)
        : 0;

    // ── Summary totals ───────────────────────────────────────────────────────
    const { totalRawPrice, totalFinalPrice, totalSavings, exactDiscountPercentage } = useMemo(() => {
        let raw = 0, final = 0;

        Object.entries(quantities).forEach(([codigo, qty]) => {
            if (qty <= 0) return;
            const gp = groupProducts.find(p => p.codigoArticulo === codigo);
            if (!gp) return;
            const originalPrice = getProductOriginalPrice(gp) || getProductPrice(gp);
            raw   += originalPrice * qty;
            final += computeProductFinalPrice(gp) * qty;
        });

        return {
            totalRawPrice: raw,
            totalFinalPrice: final,
            totalSavings: raw - final,
            exactDiscountPercentage: raw > 0 ? ((raw - final) / raw) * 100 : 0
        };
    }, [quantities, groupProducts, getProductPrice, getProductOriginalPrice, computeProductFinalPrice]);

    // ── Cart interaction ─────────────────────────────────────────────────────
    const { addProduct } = useCartContext();

    const handleQtyChange = (codigo, delta) => {
        setQuantities(prev => {
            const gp       = groupProducts.find(p => p.codigoArticulo === codigo);
            const stock    = gp?.stock;
            const currentQty = prev[codigo] ?? 0;
            const newQty   = Math.max(0, currentQty + delta);
            if (stock != null && newQty > stock) return prev;
            return { ...prev, [codigo]: newQty };
        });
        setApplied(false);
        setApplyError(null);
    };

    const handleApply = React.useCallback(async () => {
        const entries = Object.entries(quantities).filter(([, qty]) => qty > 0);
        if (entries.length === 0) return;

        setApplying(true);
        setApplyError(null);
        setApplied(false);

        try {
            for (const [codigo, qty] of entries) {
                const gp = groupProducts.find(p => p.codigoArticulo === codigo);
                if (!gp) continue;

                const precio = getProductPrice(gp);
                if (precio === 0) {
                    console.warn('[OfertasCombinables] No price found for', codigo, '— skipping');
                    continue;
                }

                const originalPrecio  = getProductOriginalPrice(gp) || precio;
                const catalogProduct  = catalogProducts.find(cp => String(cp.id) === String(gp.id));
                const realBcDiscounts = catalogProduct?.price?.bcDiscounts
                    ?? gp.bcDiscounts
                    ?? { bcDisc1: 0, bcDisc2: 0, bcDisc3: 0, bcDisc4: 0,
                         bcDisc5: 0, bcDisc6: 0, bcDisc7: 0, bcDisc8: 0, bcDisc9: 0, bcDisc10: 0 };

                await addProduct({
                    id: gp.id,
                    quantity: qty,
                    price: { sellingPrice: precio, basePrice: originalPrecio },
                    bcDiscounts: realBcDiscounts
                });
            }
            setApplied(true);
            setQuantities({});
        } catch (err) {
            console.error('[OfertasCombinables] Error adding products to cart:', err);
            setApplyError(err?.message || 'Error desconocido al añadir productos');
        } finally {
            setApplying(false);
        }
    }, [quantities, groupProducts, getProductPrice, getProductOriginalPrice, catalogProducts, addProduct]);

    // ── Notify parent ────────────────────────────────────────────────────────
    React.useEffect(() => {
        if (!onActiveItemsChange) return;
        const isActive = productPromos.length > 0 && groupProducts.length > 0 && totalQty > 0;
        onActiveItemsChange(isActive ? {
            isActive: true,
            totalQty,
            totalRawPrice,
            totalFinalPrice,
            totalSavings,
            activeTier: activeTiers.length > 0
                ? { ...activeTiers[0], totalAccumulatedPercentage: exactDiscountPercentage }
                : null,
            exactDiscountPercentage,
            nextTier,
            missingForNext,
            handleApply,
            applying,
            applied,
            applyError
        } : null);
    }, [
        productPromos, groupProducts.length, totalQty, totalRawPrice, totalFinalPrice,
        totalSavings, activeTiers, nextTier, missingForNext, applying, applied, applyError,
        handleApply, onActiveItemsChange, exactDiscountPercentage
    ]);

    if (productPromos.length === 0 || groupProducts.length === 0) return null;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="ofertas-combinables border rounded p-3 mb-3 bg-white">
            <h6 className="text-primary mb-2">🔗 Ofertas combinables</h6>

            {productPromos.map(tier => {
                const ma             = tierMinAmount(tier);
                const isAmountBased  = ma != null && ma > 0;
                const thresholdReached = isAmountBased
                    ? getTierAmount(tier) >= ma
                    : getTierQuantity(tier) >= tierMinQty(tier);
                return (
                    <p key={tier.id} className="mb-1 small"
                        style={{ color: thresholdReached ? '#198754' : '#6c757d' }}>
                        {thresholdReached ? '✅' : '○'}
                        {' '}A elegir{' '}
                        <strong>{isAmountBased ? `${ma} €` : `${tierMinQty(tier)} uds`}</strong>{' '}
                        para un <strong>{tier.discountPercentage}% de descuento</strong>
                        {tier.publicDescription && (
                            <span className="text-muted fst-italic" style={{ fontSize: '0.75em' }}>
                                {' '}— {tier.publicDescription}
                            </span>
                        )}
                    </p>
                );
            })}

            <hr className="my-2" />

            <div className="d-flex flex-column gap-2 mb-3">
                {groupProducts.map(p => {
                    const qty               = quantities[p.codigoArticulo] ?? 0;
                    const outOfStock        = p.stock != null && p.stock <= 0;
                    const originalBasePrice = getProductOriginalPrice(p) || getProductPrice(p);
                    const finalPrice        = computeProductFinalPrice(p);
                    const pctOff            = originalBasePrice > 0 ? (1 - finalPrice / originalBasePrice) * 100 : 0;
                    const hasDiscount= pctOff > 0;

                    return (
                        <div key={p.codigoArticulo}
                            className={`d-flex align-items-center gap-2 p-2 border rounded${outOfStock ? ' opacity-50' : ''}`}>
                            {p.imagenUrl && (
                                <img src={p.imagenUrl} alt={p.nombreArticulo}
                                    style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                            )}
                            <div className="flex-grow-1 small">
                                <div className="fw-bold">{p.nombreArticulo}</div>
                                <div className="text-muted d-flex align-items-center flex-wrap gap-2" style={{ fontSize: '0.75rem' }}>
                                    <span>{p.codigoArticulo}</span>
                                    {originalBasePrice > 0 && (
                                        <div className="ms-md-2" style={{ fontSize: '0.8rem' }}>
                                            {hasDiscount ? (
                                                <>
                                                    <span className="text-decoration-line-through text-muted">
                                                        {originalBasePrice.toFixed(2).replace('.', ',')} €
                                                    </span>
                                                    {' '}
                                                    <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                                                        {finalPrice.toFixed(2).replace('.', ',')} €
                                                    </span>
                                                    {' '}
                                                    <span className="badge bg-success">
                                                        -{pctOff.toFixed(0)}%
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="fw-bold">
                                                    {originalBasePrice.toFixed(2).replace('.', ',')} €
                                                </span>
                                            )}
                                            <span className="text-muted ms-1" style={{ fontSize: '0.65rem' }}>/ ud</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <button className="btn btn-outline-secondary btn-sm px-2 py-0"
                                    onClick={() => handleQtyChange(p.codigoArticulo, -1)}
                                    disabled={qty === 0 || outOfStock}>−</button>
                                <span className="px-2 fw-bold" style={{ minWidth: 24, textAlign: 'center' }}>{qty}</span>
                                <button className="btn btn-outline-secondary btn-sm px-2 py-0"
                                    onClick={() => handleQtyChange(p.codigoArticulo, 1)}
                                    disabled={outOfStock || (p.stock != null && qty >= p.stock)}>+</button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {applyError && (
                <div className="alert alert-danger mt-2 py-1 small">{applyError}</div>
            )}
        </div>
    );
};
